import React from 'react'
import { useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCustomerOrders } from '@/hooks/useCustomerOrders'
import { Clock, DollarSign, History, Bike, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'
import { useQueryClient } from '@tanstack/react-query'
import { getCustomerId } from '@/utils/customerIdentifier'
import { OrderStatusTimeline } from '@/components/OrderStatusTimeline'
import { DriverRatingDialog } from '@/components/DriverRatingDialog'

const statusConfig: Record<string, { key: string; color: string }> = {
  pending: { key: 'menu.orders.status_labels.pending', color: 'bg-yellow-500' },
  confirmed: { key: 'menu.orders.status_labels.confirmed', color: 'bg-blue-500' },
  preparing: { key: 'menu.orders.status_labels.preparing', color: 'bg-orange-500' },
  ready: { key: 'menu.orders.status_labels.ready', color: 'bg-green-500' },
  completed: { key: 'menu.orders.status_labels.completed', color: 'bg-green-600' },
  cancelled: { key: 'menu.orders.status_labels.cancelled', color: 'bg-red-500' },
}

const deliveryStatusConfig: Record<string, { key: string; color: string }> = {
  assigned: { key: 'menu.orders.delivery_status.assigned', color: 'bg-gray-500' },
  accepted: { key: 'menu.orders.delivery_status.accepted', color: 'bg-purple-500' },
  picked_up: { key: 'menu.orders.delivery_status.picked_up', color: 'bg-orange-500' },
  in_transit: { key: 'menu.orders.delivery_status.in_transit', color: 'bg-blue-500' },
  delivered: { key: 'menu.orders.delivery_status.delivered', color: 'bg-green-500' },
  cancelled: { key: 'menu.orders.delivery_status.cancelled', color: 'bg-red-500' },
}

// Fallback labels in English (used when translation key isn't loaded)
const statusFallbackLabels: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const deliveryFallbackLabels: Record<string, string> = {
  assigned: 'Assigned',
  accepted: 'Accepted',
  picked_up: 'Picked Up',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

interface OrderHistoryDialogProps {
  branchId?: string
  trigger?: React.ReactNode
  currency?: string
}

export function OrderHistoryDialog({ branchId, trigger, currency = 'USD' }: OrderHistoryDialogProps) {
  const { data: orders, isLoading, error, refetch } = useCustomerOrders(branchId)
  const { t, i18n } = useTranslation('translation')
  const queryClient = useQueryClient()
  const customerId = getCustomerId()

  // Set up real-time subscriptions for order status and delivery assignment changes
  useEffect(() => {
    if (!customerId) return

    // Subscribe to order changes (status updates)
    const ordersChannel = supabase
      .channel(`orders-${customerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `customer_id=eq.${customerId}`
        },
        (payload) => {
          console.log('📡 Order update detected:', payload)
          // Invalidate and refetch customer orders
          queryClient.invalidateQueries({ queryKey: ['customer-orders', customerId, branchId] })
          refetch()
        }
      )
      .subscribe()

    // Subscribe to delivery_assignments changes and refetch
    // We subscribe to all delivery changes, but the filter ensures we only react to relevant ones
    // by refetching customer orders (which auto-filters by customer_id)
    const deliveryChannel = supabase
      .channel(`delivery-assignments-changes`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'delivery_assignments'
        },
        (payload) => {
          console.log('📡 Delivery assignment update detected:', payload)
          // Refetch to get updated delivery status
          queryClient.invalidateQueries({ queryKey: ['customer-orders', customerId, branchId] })
          refetch()
        }
      )
      .subscribe()

    // Cleanup function to unsubscribe
    return () => {
      ordersChannel.unsubscribe()
      deliveryChannel.unsubscribe()
    }
  }, [customerId, branchId, queryClient, refetch])

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString()
  const formatPrice = (amount: number) => `${currency} ${amount != null ? amount.toFixed(2) : '0.00'}`

  // Calculate actual delivery time in minutes
  const calculateDeliveryTime = (delivery: any) => {
    if (!delivery?.assigned_at || !delivery?.delivered_at) return null
    
    const assignedTime = new Date(delivery.assigned_at).getTime()
    const deliveredTime = new Date(delivery.delivered_at).getTime()
    const minutes = Math.round((deliveredTime - assignedTime) / (1000 * 60))
    
    return minutes
  }

  // Format delivery time with translation
  const getDeliveryTimeLabel = (minutes: number | null) => {
    if (minutes === null) return null
    
    if (i18n.language === 'ar') {
      if (minutes < 60) {
        return `${minutes} دقيقة`
      }
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return mins > 0 ? `${hours}س ${mins}دقيقة` : `${hours}س`
    } else {
      if (minutes < 60) {
        return `${minutes}m`
      }
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
  }

  // Direct translations to fix the key issue
  const getTranslation = (key: string) => {
    switch (key) {
      case 'my_orders':
        return i18n.language === 'ar' ? 'طلباتي' : 'My Orders'
      case 'no_orders':
        return i18n.language === 'ar' ? 'لا توجد طلبات بعد' : 'No orders yet'
      case 'error_loading':
        return i18n.language === 'ar' ? 'خطأ في تحميل الطلبات' : 'Error loading orders'
      case 'items':
        return i18n.language === 'ar' ? 'العناصر' : 'Items'
      default:
        return key
    }
  }

  // Get translated status label - uses i18n to lookup translated values
  const getStatusLabel = (status: string) => {
    // Direct i18n lookup with language-specific fallback
    if (i18n.language === 'ar') {
      const arTranslations: Record<string, string> = {
        pending: 'قيد الانتظار',
        confirmed: 'مؤكد',
        preparing: 'قيد التحضير',
        ready: 'جاهز',
        completed: 'مكتمل',
        cancelled: 'ملغي'
      }
      return arTranslations[status] || status
    }
    return statusFallbackLabels[status] || status
  }

  // Get translated delivery status label - uses i18n to lookup translated values
  const getDeliveryStatusLabel = (deliveryStatus: string) => {
    // Direct i18n lookup with language-specific fallback
    if (i18n.language === 'ar') {
      const arTranslations: Record<string, string> = {
        assigned: 'مُكلف',
        accepted: 'مقبول',
        picked_up: 'تم الاستلام',
        in_transit: 'في الطريق',
        delivered: 'تم التسليم',
        cancelled: 'ملغي'
      }
      return arTranslations[deliveryStatus] || deliveryStatus
    }
    return deliveryFallbackLabels[deliveryStatus] || deliveryStatus
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline">
            <History className="h-4 w-4 mr-2" />
            {getTranslation('my_orders')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getTranslation('my_orders')}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">{getTranslation('error_loading')}</div>
        ) : orders && Array.isArray(orders) && orders.length > 0 ? (
          <div className="space-y-4">
            {(orders as any[]).map(order => (
              <Card key={order.id} className="overflow-hidden">
                {/* Main Content */}
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start gap-4">
                    {/* Left - Order number and date */}
                    <div>
                      <CardTitle className="text-lg font-bold">#{order.order_number}</CardTitle>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(order.created_at || '')}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Left Column - Timeline */}
                    <div className="flex-1 lg:min-w-[280px]">
                      <OrderStatusTimeline 
                        status={order.status} 
                        isDelivery={false}
                        deliveryStatus={order.delivery_assignments && order.delivery_assignments.length > 0 ? order.delivery_assignments[0].assignment_status : null}
                        hasDelivery={order.order_type === 'delivery' || (order.delivery_assignments && order.delivery_assignments.length > 0)}
                      />
                      {order.delivery_assignments && order.delivery_assignments.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              <Bike className="h-4 w-4 text-blue-600" />
                              <span className="text-xs font-semibold text-gray-600">
                                {order.delivery_assignments[0].driver?.name || 'Delivery in progress'}
                              </span>
                              {/* Show delivery time if delivered */}
                              {order.delivery_assignments[0].assignment_status === 'delivered' && calculateDeliveryTime(order.delivery_assignments[0]) && (
                                <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-green-50 border border-green-200 rounded">
                                  <Zap className="h-3 w-3 text-green-600" />
                                  <span className="text-xs font-medium text-green-700">
                                    {getDeliveryTimeLabel(calculateDeliveryTime(order.delivery_assignments[0]))}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Show rating button only if delivered */}
                            {order.delivery_assignments[0].assignment_status === 'delivered' && (
                              <DriverRatingDialog
                                assignmentId={order.delivery_assignments[0].id}
                                driverId={order.delivery_assignments[0].driver_id}
                                driverName={order.delivery_assignments[0].driver?.name || 'Driver'}
                                orderId={order.id}
                                branchId={order.branch_id}
                                existingRating={order.delivery_assignments[0].customer_rating || 0}
                                existingFeedback={order.delivery_assignments[0].customer_feedback || ''}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Order Details */}
                    <div className="flex-1 space-y-3">
                      {/* Address and Phone */}
                      {(order.customer_address || order.customer_phone) && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                          {order.customer_address && (
                            <div className="text-xs text-gray-700">
                              <span className="font-semibold text-blue-700">Address:</span> {order.customer_address}
                            </div>
                          )}
                          {order.customer_phone && (
                            <div className="text-xs text-gray-700">
                              <span className="font-semibold text-blue-700">Phone:</span> {order.customer_phone}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Items List */}
                      {order.order_items && order.order_items.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-100 px-2.5 py-1.5 border-b">
                            <h4 className="text-xs font-semibold text-gray-800">{getTranslation('items')}</h4>
                          </div>
                          <div className="divide-y max-h-[200px] overflow-y-auto">
                            {order.order_items.map(item => (
                              <div key={item.id} className="p-2.5">
                                <div className="flex justify-between items-start mb-0.5">
                                  <span className="text-xs font-medium text-gray-800">
                                    {item.quantity}x {item.menu_items?.name || item.menu_item_id}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-900">
                                    {formatPrice(item.total_price)}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatPrice(item.unit_price)} each
                                </div>
                                {item.notes && (
                                  <div className="text-xs text-gray-600 mt-0.5 italic">
                                    Note: {item.notes}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Total and Order Notes */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-2.5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-bold text-gray-800">Total Amount</span>
                          <span className="text-base font-bold text-green-700">
                            {formatPrice(order.total_amount)}
                          </span>
                        </div>
                        {order.notes && (
                          <div className="text-xs text-gray-700 border-t border-green-200 pt-1.5 mt-1.5">
                            <span className="font-semibold">Note: </span> {order.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">{getTranslation('no_orders')}</div>
        )}
      </DialogContent>
    </Dialog>
  )
} 